import { AgGridAngular } from '@ag-grid-community/angular';
import { GridOptions, RowNode } from '@ag-grid-community/core';
import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import dayjs from 'dayjs';
import { BehaviorSubject, firstValueFrom, Subject } from 'rxjs';
import { startWith, switchMap } from 'rxjs/operators';

import { DataSource, GqlService } from '@shared/services/gql.service';
import { OverlayService } from '@shared/services/overlay.service';
import { Utils } from '@shared/utils/utils';

import { AgIntegrationsConnectComponent } from './state/ag-integrations-connect.component';
import { AgIntegrationsVendorComponent } from './state/ag-integrations-vendor.component';
import { IntegrationsQuery } from './state/integrations.query';
import { IntegrationsService } from './state/integrations.service';
import { IntegrationsState } from './state/integrations.store';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [AsyncPipe, AgGridAngular],
  selector: 'aux-integrations',
  styles: [
    `
      ::ng-deep .integration-table .ag-cell.grid-cell {
        border-top-width: 0 !important;
        border-left-width: 0 !important;
        border-right-width: 0 !important;
        border-bottom-width: 1px !important;
        border-color: var(--aux-gray-dark) !important;
      }

      ::ng-deep .integration-table .ag-header {
        border: 1px solid var(--aux-gray-dark) !important;
        border-radius: 0 0 0 0 !important;
        border-left-width: 0 !important;
      }

      ::ng-deep .integration-table .ag-header-row,
      ::ng-deep .integration-table .ag-header-cell {
        overflow: unset;
      }

      ::ng-deep .integration-table .ag-header-cell-text,
      ::ng-deep .integration-table .ag-row {
        font-size: 1rem;
      }

      ::ng-deep .integration-table .ag-header-cell {
        border-left: 1px solid var(--aux-gray-dark) !important;
        text-align: center;
      }

      ::ng-deep .integration-table .ag-header-cell-label {
        justify-content: center;
      }

      ::ng-deep .integration-table .ag-icon-filter {
        width: 0 !important;
      }
    `,
  ],
  templateUrl: './integrations.component.html',
})
export class IntegrationsComponent implements OnDestroy, OnInit {
  readonly integrationsQuery = inject(IntegrationsQuery);

  private readonly destroyRef = inject(DestroyRef);
  private readonly gqlService = inject(GqlService);
  private readonly integrationService = inject(IntegrationsService);
  private readonly overlayService = inject(OverlayService);

  gridData$ = new BehaviorSubject<IntegrationsState['items']>([]);
  gridOptions: GridOptions = {
    columnDefs: [
      {
        cellClass: 'grid-cell items-center',
        cellRenderer: AgIntegrationsVendorComponent,
        headerName: 'Vendor',
        width: 400,
      },
      {
        cellClass: 'grid-cell items-center',
        field: 'type',
        headerName: 'Type',
        sortable: false,
        width: 200,
      },
      {
        cellClass: 'grid-cell items-center',
        cellRenderer: AgIntegrationsConnectComponent,
        cellRendererParams: {
          hideRadioButton: true,
          logInClickFN: ({ rowNode }: { rowNode: RowNode }) => {
            void this.oauthOpenLoginWindow(rowNode?.data?.data_source);
          },
          logOutClickFN: ({ rowNode }: { rowNode: RowNode }) => {
            void this.oauthDisconnect(rowNode?.data?.data_source);
          },
        },
        headerName: 'Connection Status',
        sortable: false,
        width: 250,
      },
      {
        cellClass: 'grid-cell items-center',
        field: 'last_connection_date',
        headerName: 'Last Call',
        sortable: false,
        valueFormatter: (val) => {
          return val.value
            ? `${dayjs(new Date(val.value)).format('YYYY-MM-DD')} at ${dayjs(
                new Date(val.value)
              ).format('HH:mm')}`
            : Utils.zeroHyphen;
        },
        width: 248,
      },
    ],
    headerHeight: 45,
    overlayNoRowsTemplate:
      '<div class="text-xl">Please select an available integration to configure.</div>',
    rowHeight: 50,
  };
  integrationVendors = [
    {
      logo: 'assets/img/netsuite.png',
      name: 'NetSuite',
      type: 'General Ledger / ERP',
    },
    {
      logo: 'assets/img/oracle-fusion.png',
      name: 'Oracle Fusion',
      type: 'General Ledger / ERP',
    },
    {
      logo: 'assets/img/quickbooks.png',
      name: 'Quickbooks',
      type: 'Invoices',
    },
    {
      logo: 'assets/img/coupa.png',
      name: 'Coupa',
      type: 'Invoices',
    },
    {
      logo: 'assets/img/beanworks.png',
      name: 'Beanworks',
      type: 'Invoices',
    },
    {
      logo: 'assets/img/sap.png',
      name: 'SAP',
      type: 'ERP',
    },
    {
      logo: 'assets/img/microsoft-dynamics.png',
      name: 'Microsoft Dynamics',
      type: 'General Ledger / ERP',
    },
    {
      logo: 'assets/img/sage-intacct.png',
      name: 'Sage Intacct',
      type: 'General Ledger / ERP',
    },
    {
      logo: 'assets/img/adaptive-planning.png',
      name: 'Adaptive Planning',
      type: 'General Ledger / ERP',
    },
    {
      logo: 'assets/img/rave.png',
      name: 'Rave',
      type: 'EDC',
    },
    {
      logo: 'assets/img/veeva.png',
      name: 'Veeva',
      type: 'CDMS',
    },
  ];
  loading$ = new BehaviorSubject(false);
  refreshConnections$ = new Subject();

  private oauthCallbackOrigin = '';

  ngOnInit(): void {
    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    window.addEventListener('message', this.oauthReceiveMessage, false);

    this.integrationsQuery
      .select()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ items }) => {
        this.gridData$.next(items);
      });

    this.refreshConnections$
      .pipe(
        startWith(null),
        takeUntilDestroyed(this.destroyRef),
        switchMap(() => {
          return this.integrationService.getIntegrations();
        })
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    window.removeEventListener('message', this.oauthReceiveMessage);
  }

  async oauthDisconnect(dataSource: string) {
    const response = await firstValueFrom(
      this.gqlService.revokeOAuthToken$(dataSource as DataSource)
    );

    if (response.success && response.data) {
      if (!response.data.is_connected) {
        this.overlayService.success('Logout complete');
        this.refreshConnections$.next(null);
      }
    } else {
      this.overlayService.error(response.errors);
    }
  }

  async oauthOpenLoginWindow(dataSource: string) {
    const response = await firstValueFrom(
      this.gqlService.getOAuthLoginUrl$(dataSource as DataSource)
    );

    if (response.success && response.data) {
      this.oauthCallbackOrigin = response.data.callback_origin;
      window.open(response.data.login_url);
    } else {
      this.overlayService.error(response.errors);
    }
  }

  private oauthReceiveMessage = async (
    event: MessageEvent<{
      params: string;
      type: string;
    }>
  ) => {
    if (!this.oauthCallbackOrigin || event.origin !== this.oauthCallbackOrigin) {
      return;
    }

    if (event.data.type === 'oauth') {
      const response = await firstValueFrom(this.gqlService.exchangeOAuthCode$(event.data.params));

      if (response.success && response.data) {
        if (response.data.is_connected) {
          this.overlayService.success('Login complete');
          this.refreshConnections$.next(null);
        }
      } else {
        this.overlayService.error(response.errors);
      }
    }
  };
}
